//  This component displays a summary of adherence + alerts in the alerts
//  Not to confuse with actual, separate adherence monitor page

import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Skeleton,
  useTheme,
  Chip,
  Stack,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Tooltip,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Schedule as ScheduleIcon,
  Warning as AlertIcon,
  CheckCircle as CheckCircleIcon,
  FiberManualRecord as DotIcon,
} from "@mui/icons-material";
import { AlertsService } from "../../services/alertsService";

interface AdherenceMonitorSummaryProps {
  studyId: string;
  refreshTrigger?: number;
}

interface SummaryStats {
  lastModelRun: string | null;
  hoursAgo: number | null;
  nextModelRunAt: string;
  pendingAlerts: number;
  pendingParticipants: number;
  avgAdherence: number; // adherence % (0-100)
  maxAdherence: number; // adherence % (0-100)
  lowestAdherenceParticipants: Array<{
    participantId: string;
    adherence: number;
  }>;
  weeklyActions: {
    contacted: number;
    resolved: number;
    falsePositives: number;
  };
}

const AdherenceMonitorSummary: React.FC<AdherenceMonitorSummaryProps> = ({
  studyId,
  refreshTrigger = 0,
}) => {
  const theme = useTheme();
  const [stats, setStats] = useState<SummaryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await AlertsService.getSummaryStats(studyId);
        setStats(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load summary");
      } finally {
        setLoading(false);
      }
    };

    if (studyId) {
      fetchSummary();
    }
  }, [studyId, refreshTrigger]);

  // const formatDateTime = (dateStr: string | null) => {
  //   if (!dateStr) return "—";
  //   try {
  //     const date = new Date(dateStr);
  //     return date.toLocaleString("en-US", {
  //       month: "short",
  //       day: "numeric",
  //       hour: "2-digit",
  //       minute: "2-digit",
  //     });
  //   } catch {
  //     return "—";
  //   }
  // };

  // Format datetime in LA timezone (PDT/PST)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const formatDateTimeInLA = (dateStr: string | null) => {
    if (!dateStr) return "—";
    try {
      const date = new Date(dateStr);
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "America/Los_Angeles",
      });
    } catch {
      return "—";
    }
  };

  // Calculate time to next 1 AM LA time (PDT/PST)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getTimeToNextRun = () => {
    try {
      const now = new Date();

      // Get current LA time components
      const laFormatter = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Los_Angeles",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const parts = laFormatter.formatToParts(now);
      // const laYear = parseInt(
      //   parts.find((p) => p.type === "year")?.value || "0"
      // );
      // const laMonth = parseInt(
      //   parts.find((p) => p.type === "month")?.value || "0"
      // );
      // const laDay = parseInt(parts.find((p) => p.type === "day")?.value || "0");
      // const laHour = parseInt(
      //   parts.find((p) => p.type === "hour")?.value || "0"
      // );

      // Create date string for current LA date
      const dateStr = new Date(now).toLocaleString("en-US", {
        timeZone: "America/Los_Angeles",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });

      // Parse date string to get year, month, day
      const [month, day, year] = dateStr.split("/");

      // Create a date for today at 1 AM in local timezone, then convert to UTC
      const todayAt1Am = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        1,
        0,
        0,
      );

      // convert to LA time to find the offset
      const todayAt1AmInLA = new Date(
        todayAt1Am.toLocaleString("en-US", {
          timeZone: "America/Los_Angeles",
        }),
      );

      // Calculate difference to find UTC offset
      const utcOffset = todayAt1Am.getTime() - todayAt1AmInLA.getTime();

      // Create the actual next 1 AM UTC time
      let next1AmUtc = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        1,
        0,
        0,
      );
      next1AmUtc = new Date(next1AmUtc.getTime() + utcOffset);

      // If that time is in the past, use tomorrow
      if (next1AmUtc.getTime() <= now.getTime()) {
        next1AmUtc.setDate(next1AmUtc.getDate() + 1);
      }

      const diffMs = next1AmUtc.getTime() - now.getTime();
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      if (hours > 0) return `${hours}h ${minutes}m`;
      if (minutes > 0) return `${minutes}m`;
      return "< 1m";
    } catch {
      return "—";
    }
  };

  // Get next 1 AM LA time (PDT/PST) formatted
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getNextRunTimeFormatted = () => {
    try {
      const now = new Date();

      // Get current LA date
      const dateStr = new Date(now).toLocaleString("en-US", {
        timeZone: "America/Los_Angeles",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });

      const [month, day, year] = dateStr.split("/");

      // Create a date for today at 1 AM in local timezone, then convert to UTC
      const todayAt1Am = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        1,
        0,
        0,
      );

      // Get what time this "local" 1 AM date actually represents in LA
      const todayAt1AmInLA = new Date(
        todayAt1Am.toLocaleString("en-US", {
          timeZone: "America/Los_Angeles",
        }),
      );

      // Calculate difference to find UTC offset
      const utcOffset = todayAt1Am.getTime() - todayAt1AmInLA.getTime();

      // Create the actual next 1 AM UTC time
      let next1AmUtc = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        1,
        0,
        0,
      );
      next1AmUtc = new Date(next1AmUtc.getTime() + utcOffset);

      // If that time is in the past, use tomorrow
      if (next1AmUtc.getTime() <= now.getTime()) {
        next1AmUtc.setDate(next1AmUtc.getDate() + 1);
      }

      return next1AmUtc.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "America/Los_Angeles",
      });
    } catch {
      return "—";
    }
  };

  if (loading) {
    return (
      <Card sx={{ mb: 3, backgroundColor: theme.palette.background.paper }}>
        <CardContent>
          <Grid container spacing={2}>
            {[...Array(6)].map((_, i) => (
              <Grid item xs={12} sm={6} md={3} lg={2} key={i}>
                <Skeleton variant="rectangular" height={100} />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        sx={{
          mb: 3,
          backgroundColor: theme.palette.error.light,
          border: `1px solid ${theme.palette.error.main}`,
        }}
      >
        <CardContent>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.error.dark, fontWeight: 500 }}
          >
            ⚠️ Failed to load adherence monitor: {error}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  // Helper component for stat items within sections
  const StatItem: React.FC<{
    title: string;
    value: React.ReactNode;
    subtitle?: string;
    icon?: React.ReactNode;
    color?: string;
  }> = ({ title, value, subtitle, icon, color = "primary" }) => {
    const getColor = (colorName: string) => {
      switch (colorName) {
        case "info":
          return theme.palette.info.main;
        case "warning":
          return theme.palette.warning.main;
        case "error":
          return theme.palette.error.main;
        case "success":
          return theme.palette.success.main;
        default:
          return theme.palette.primary.main;
      }
    };

    return (
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        {icon && (
          <Box sx={{ mt: 0.25, display: "flex", color: getColor(color) }}>
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              textTransform: "uppercase",
              fontWeight: 600,
              fontSize: "0.7rem",
              letterSpacing: "0.5px",
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              mt: 0.5,
              color: theme.palette.text.primary,
            }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                mt: 0.5,
                display: "block",
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Card
      sx={{
        mb: 3,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[1],
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Header */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: 2,
            color: theme.palette.text.primary,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <TrendingUpIcon />
          Adherence Monitor Summary
        </Typography>

        {/* Model Run Status Section
        <Box
          sx={{
            pb: 2,
            mb: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.secondary,
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Model Status
            </Typography>
            <Tooltip title="LA time refers to PDT (Pacific Daylight Time) during daylight saving or PST (Pacific Standard Time) during winter">
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  cursor: "help",
                  textDecoration: "underline dotted",
                  fontSize: "0.65rem",
                }}
              >
                (LA time)
              </Typography>
            </Tooltip>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <StatItem
                title="Last Model Run"
                value={
                  stats.hoursAgo !== null ? (
                    <Stack spacing={0.5}>
                      <span>{stats.hoursAgo}h ago</span>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        ({formatDateTimeInLA(stats.lastModelRun)} LA time)
                      </Typography>
                    </Stack>
                  ) : (
                    "—"
                  )
                }
                icon={<ScheduleIcon fontSize="small" />}
                color="info"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StatItem
                title="Next Model Run (1 AM LA time)"
                value={getTimeToNextRun()}
                subtitle={getNextRunTimeFormatted() + " LA time"}
                icon={<ScheduleIcon fontSize="small" />}
                color="info"
              />
            </Grid>
          </Grid>
        </Box>
        */}

        {/* Alerts & Risk Section */}
        <Box
          sx={{
            pb: 2,
            mb: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 1.5,
              color: theme.palette.text.secondary,
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Current State
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <StatItem
                title="Pending Alerts"
                value={
                  <Stack spacing={0.5}>
                    <span>{stats.pendingAlerts}</span>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      {stats.pendingParticipants} participant
                      {stats.pendingParticipants !== 1 ? "s" : ""}
                    </Typography>
                  </Stack>
                }
                icon={<AlertIcon fontSize="small" />}
                color="warning"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StatItem
                title="Avg Adherence"
                value={
                  <>
                    <Box sx={{ fontWeight: 700 }}>
                      Avg: {stats.avgAdherence.toFixed(1)}% | Max:{" "}
                      {stats.maxAdherence.toFixed(1)}%
                    </Box>
                    {stats.lowestAdherenceParticipants.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Lowest adherence:
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          sx={{ flexWrap: "wrap" }}
                        >
                          {stats.lowestAdherenceParticipants
                            .slice(0, 2)
                            .map((p) => (
                              <Chip
                                key={p.participantId}
                                label={`${p.participantId.slice(-4)}: ${p.adherence.toFixed(0)}%`}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: "0.65rem",
                                  height: "20px",
                                  borderColor: theme.palette.error.main,
                                  color: theme.palette.error.main,
                                }}
                              />
                            ))}
                        </Stack>
                      </Box>
                    )}
                  </>
                }
                icon={<TrendingUpIcon fontSize="small" />}
                color="error"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Weekly Actions Section */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              mb: 1.5,
              color: theme.palette.text.secondary,
              fontSize: "0.75rem",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Actions This Week
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <StatItem
                title="Contacted"
                value={stats.weeklyActions.contacted}
                icon={<DotIcon fontSize="small" />}
                color="info"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatItem
                title="Resolved"
                value={stats.weeklyActions.resolved}
                icon={<CheckCircleIcon fontSize="small" />}
                color="success"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatItem
                title="False Positives"
                value={stats.weeklyActions.falsePositives}
                icon={<DotIcon fontSize="small" />}
                color="warning"
              />
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AdherenceMonitorSummary;
